import styles from './MainPage.module.scss';
import { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { GameSettingsContext } from '../../modules/socketContexts';
import { getNoun } from '../../modules/util';
import {
    postNative,
    getInviteBoot,
    nativeInvites,
    inviteJoinProof,
    isInviteRoomId,
    clearInvitationId,
    reportInviteJoinFailed,
    subscribeNativeEvents,
} from '../../modules/nativeBridge';
import BatakTutorial from '../../components/BatakTutorial/BatakTutorial';
import {
    openInvitePanel,
    closeInvitePanel,
    showGameInviteDialog,
    dismissGameInviteDialog,
    showInviteToast,
} from '../../modules/gameInvites';

// Tasarım görselleri (BATAK TASARIM klasöründen optimize kopyalar) — yazılar
// görsellerin içinde bakılı olduğundan buton metinleri img olarak basılır.
import imgLogo from '../../assets/batak_logo.png';
import imgQuickPlay from '../../assets/btn_hizli_oyna.png';
import imgModeEsli from '../../assets/mode_esli.png';
import imgModeTekli from '../../assets/mode_tekli.png';
import imgModeGommeli from '../../assets/mode_gommeli.png';
import imgModeCheck from '../../assets/mode_check.png';
import imgMasalar from '../../assets/masalar_title.png';
import imgInvite from '../../assets/btn_davet_et.png';
import imgExit from '../../assets/btn_cikis.png';
import ExitConfirmDialog from '../../components/ExitConfirmDialog/ExitConfirmDialog';

// ── Open tables ───────────────────────────────────────────────────────────────
// The lobby is a list of tables you can see and pick, the way the other games
// do it — not a blind queue. Quick match stays available for people who just
// want to be seated. Görünüm tasarımdaki tek satırlık "masa bandı": amblem,
// koltuk daireleri, doluluk ve aksiyon butonu (isim listesi tasarımda yok).
const RoomList = ({ rooms, onJoin, onWatch, onReturn, myUserId, busy }) => {
    if (!rooms.length) {
        return <div className={styles.roomsEmpty}>Bu modda açık masa yok — Hızlı Oyna ile başla</div>;
    }
    return (
        <div className={styles.roomList}>
            {rooms.map(r => {
                const running = !r.joinable && r.watchable;
                // A seat carrying your id is yours whatever is sitting in it —
                // a bot may only be holding it. That takes priority over both
                // joining and watching, so you can always get back to your game.
                const mySeat = myUserId && r.players.some(p => p.userId === myUserId);
                const action = mySeat ? 'return' : (r.joinable ? 'join' : 'watch');
                return (
                    <button
                        key={r.roomId}
                        className={`${styles.roomCard} ${running ? styles.roomCardLive : ''} ${mySeat ? styles.roomCardMine : ''}`}
                        onClick={() => {
                            if (action === 'return') onReturn(r.roomId);
                            else if (action === 'join') onJoin(r.roomId);
                            else onWatch(r.roomId);
                        }}
                        disabled={busy || (action === 'watch' && !r.watchable)}
                    >
                        <span className={styles.roomEmblem} aria-hidden>♣</span>

                        {/* Every chair, so you can see who is there before sitting down */}
                        <div className={styles.roomSeats}>
                            {Array.from({ length: r.capacity }, (_, i) => {
                                const p = r.players[i];
                                return (
                                    <span
                                        key={i}
                                        className={[
                                            styles.roomSeat,
                                            p ? styles.roomSeatTaken : '',
                                            p?.isBot ? styles.roomSeatBot : '',
                                        ].filter(Boolean).join(' ')}
                                        title={p ? (p.isBot ? `${p.displayName} (bot)` : p.displayName) : 'Boş'}
                                        style={p?.frameUrl ? { position: 'relative', overflow: 'visible' } : undefined}
                                    >
                                        {p
                                            ? (p.isBot
                                                ? '🤖'
                                                : (p.avatarUrl
                                                    ? <><img src={'/avatar-proxy?url=' + encodeURIComponent(p.avatarUrl)} alt="" className={styles.roomSeatImg} style={p.frameUrl ? { borderRadius: '50%' } : undefined} />{p.frameUrl ? <img src={p.frameUrl} alt="" style={{ position: 'absolute', left: '50%', top: '50%', width: '122%', height: '122%', transform: 'translate(-50%,-50%)', objectFit: 'contain', pointerEvents: 'none', borderRadius: 0 }} /> : null}</>
                                                    : (p.displayName?.charAt(0)?.toUpperCase() || '?')))
                                            : ''}
                                    </span>
                                );
                            })}
                        </div>

                        <span className={styles.roomCount}>{r.count}/{r.capacity}</span>
                        {running && <span className={styles.roomLiveTag}>OYUNDA</span>}
                        {r.watcherCount > 0 && (
                            <span className={styles.roomWatchers}>👁 {r.watcherCount}</span>
                        )}

                        <span className={
                            action === 'return' ? styles.roomReturn
                            : action === 'join' ? styles.roomJoin
                            : styles.roomWatch
                        }>
                            {action === 'return' ? 'Geri Dön' : action === 'join' ? 'Katıl' : 'İzle'}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

// ── Lobby overlay ─────────────────────────────────────────────────────────────
const LobbyOverlay = ({ lobbyData, playerName, onCancel, onInvite }) => {
    const [dots, setDots] = useState('');

    useEffect(() => {
        const id = setInterval(() =>
            setDots(d => d.length >= 3 ? '' : d + '.'), 500);
        return () => clearInterval(id);
    }, []);

    const capacity = lobbyData?.capacity || 4;
    const players  = lobbyData?.players  || [];
    const slots    = Array.from({ length: capacity }, (_, i) => players[i] || null);
    const count    = players.length;

    return (
        <div className={styles.lobbyCard}>
            <div className={styles.lobbyPulse} />
            <p className={styles.lobbyTitle}>Rakip aranıyor{dots}</p>
            <p className={styles.lobbyCount}>{count}/{capacity} oyuncu</p>

            <div className={styles.playerSlots}>
                {slots.map((p, i) => (
                    <div
                        key={i}
                        className={`${styles.playerSlot} ${p ? styles.filled : styles.empty}`}
                    >
                        {p ? (
                            <>
                                <span className={styles.slotAvatar} style={p.frameUrl ? { position: 'relative', overflow: 'visible' } : undefined}>
                                    {p.avatarUrl
                                        ? <><img src={'/avatar-proxy?url=' + encodeURIComponent(p.avatarUrl)} alt="" className={styles.slotAvatarImg} style={p.frameUrl ? { borderRadius: '50%' } : undefined} />{p.frameUrl ? <img src={p.frameUrl} alt="" style={{ position: 'absolute', left: '50%', top: '50%', width: '122%', height: '122%', transform: 'translate(-50%,-50%)', objectFit: 'contain', pointerEvents: 'none', borderRadius: 0 }} /> : null}</>
                                        : (p.isBot ? '🤖' : p.displayName?.charAt(0)?.toUpperCase() || '👤')}
                                </span>
                                <span className={`${styles.slotName} ${p.displayName === playerName ? styles.myName : ''}`}>
                                    {p.displayName}
                                    {p.displayName === playerName && <span className={styles.youTag}> (sen)</span>}
                                </span>
                                {p.isBot && <span className={styles.botTag}>Bot</span>}
                            </>
                        ) : (
                            <span className={styles.slotWaiting}>Bekleniyor{dots}</span>
                        )}
                    </div>
                ))}
            </div>

            {onInvite && (
                <button className={styles.lobbyInviteBtn} onClick={onInvite} aria-label="Arkadaşlarını davet et">
                    <img src={imgInvite} alt="Arkadaşlarını Davet Et" draggable={false} />
                </button>
            )}
            <button className={styles.cancelBtn} onClick={onCancel}>
                İptal
            </button>
        </div>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const MainPage = () => {
    const navigate = useNavigate();
    const [playerName, updatePlayerName, nativeProfile] = useContext(GameSettingsContext);
    const [nameInput, setNameInput] = useState(playerName || getNoun());
    const [phase, setPhase] = useState('idle'); // 'idle' | 'connecting' | 'waiting'
    const [lobbyData, setLobbyData] = useState(null);
    const [openRooms, setOpenRooms] = useState([]);
    const [lobbyError, setLobbyError] = useState('');
    const socketRef = useRef(null);
    // Oyun modu: 'partner' (eşli) | 'solo' (tekli). Sekmeden seçilir, hem masa
    // listesini filtreler hem de Hızlı Oyna'nın hangi masaya oturacağını belirler.
    const MODES = ['partner', 'solo', 'gomme'];
    const [exitConfirm, setExitConfirm] = useState(false);
    const [mode, setMode] = useState(() => {
        try {
            const v = localStorage.getItem('batak_mode');
            return MODES.includes(v) ? v : 'partner';
        } catch (e) { return 'partner'; }
    });
    const modeRef = useRef(mode);
    modeRef.current = mode;
    // Davet akışı: en güncel seat/oda listesine soket dinleyicilerinden erişim
    const seatRef = useRef(null);
    const currentRoomRef = useRef(null);
    // Oturulup beklenen masa (lobbyUpdate): davet bu masaya gider ve hold'u başlatır.
    const seatedRoomRef = useRef(null);
    const inviteBootDoneRef = useRef(false);

    // Lobi müziği: oyun masası (BelotePage) hariç her ekranda çalar. Ses
    // Flutter'da loop'lu çalınır (playSfx köprüsü); masaya geçince bu sayfa
    // unmount olur ve cleanup durdurur, lobiye dönünce yeniden başlar.
    useEffect(() => {
        postNative('playSfx', { name: 'musicLobby', volume: 0.6, loop: true });
        return () => postNative('stopSfx', { name: 'musicLobby' });
    }, []);

    // One lobby socket for the whole main screen: it keeps the table list live
    // while browsing and is the same connection that seats you.
    useEffect(() => {
        const serverUrl = process.env.REACT_APP_SERVER_URL || '';
        const socket = io(serverUrl, {
            query: {
                action: 'connectToServer',
                client: nativeProfile?.userId || `${Date.now()}_${Math.random()}`,
                userId: nativeProfile?.userId || '',
                id: nativeProfile?.id || '',
                uuid: nativeProfile?.uuid || '',
                avatarUrl: nativeProfile?.avatarUrl || '',
            },
            transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        socket.on('lobbyRooms', ({ rooms }) => {
            setOpenRooms(rooms || []);
        });
        socket.on('lobbyUpdate', (data) => {
            if (data?.roomId) {
                seatedRoomRef.current = data.status === 'waiting' ? data.roomId : null;
                clearInvitationId();
            }
            setLobbyData(data);
        });
        socket.on('connect_error', () => setPhase('idle'));

        socket.on('onError', (err) => {
            // Davetle gelinen masaya oturulamadı → join-failed (bir kez).
            reportInviteJoinFailed(err);
            currentRoomRef.current = null;
            setPhase('idle');
            setLobbyData(null);
            if (err?.code === 'insufficient_coins') {
                // Wallet is the app's business — hand it straight to the paywall.
                postNative('onError', { code: 'insufficient_coins', message: err.message || 'Yetersiz coin' });
                setLobbyError('Yeterli coinin yok.');
            } else {
                setLobbyError(err?.message || 'Bir hata oluştu');
            }
            setTimeout(() => setLobbyError(''), 4000);
        });

        // Oda sahibi bekleyen masadan attı.
        socket.on('kicked', () => {
            currentRoomRef.current = null;
            seatedRoomRef.current = null;
            setPhase('idle');
            setLobbyData(null);
            setLobbyError('Oda sahibi seni masadan çıkardı');
            setTimeout(() => setLobbyError(''), 4000);
        });

        socket.on('gameReady', ({ roomId }) => {
            clearInvitationId();
            socketRef.current = null;
            socket.disconnect();
            navigate(`/game/${roomId}`);
        });

        // ── Oyun davetleri (soketten anlık) ──────────────────────────────────
        // Kimliği kaydettir: ana sayfada beklerken de davet hedefi olunabilsin
        socket.on('connect', () => {
            socket.emit('presence:register', {
                userId: nativeProfile?.userId || '',
                displayName: nativeProfile?.playerName || '',
                avatarUrl: nativeProfile?.avatarUrl || '',
                callerId: nativeProfile?.callerId || '',
            });

            // Davet kabulüyle açılış (uygulamadan veya oyun içinden reload):
            // kabul cevabını ilet + doğrudan masaya otur (bir kez).
            if (!inviteBootDoneRef.current) {
                inviteBootDoneRef.current = true;
                const boot = getInviteBoot();
                if (boot.inviteFromUserId) {
                    socket.emit('gameInviteResponse', {
                        targetUserId: boot.inviteFromUserId,
                        accepted: true,
                        roomId: boot.joinRoomId || null,
                    });
                }
                if (boot.joinRoomId && seatRef.current) {
                    seatRef.current(boot.joinRoomId);
                }
            } else if (isInviteRoomId(currentRoomRef.current) && seatRef.current) {
                // Yeniden bağlanma: davet masasında bekliyorduk; yeni soket koltuğa
                // bağlansın (token + cihaz ile, sunucu yine doğrular).
                seatRef.current(currentRoomRef.current);
            }
        });

        socket.on('gameInviteReceived', (d) => {
            // Yeni uygulama daveti kendi banner'ıyla gösterir.
            if (nativeInvites) return;
            showGameInviteDialog(d, {
                onAccept: () => {
                    socket.emit('gameInviteResponse', {
                        targetUserId: d.fromUserId,
                        accepted: true,
                        roomId: d.roomId || null,
                    });
                    closeInvitePanel();
                    if (seatRef.current) seatRef.current(d.roomId || null);
                },
                onReject: () => socket.emit('gameInviteResponse', {
                    targetUserId: d.fromUserId,
                    accepted: false,
                }),
            });
        });

        socket.on('gameInviteResult', (d) => {
            if (d.accepted) {
                // Davet edilen kabul etti → davet eden de aynı masaya otursun
                if (d.roomId && currentRoomRef.current !== d.roomId && seatRef.current) {
                    closeInvitePanel();
                    seatRef.current(d.roomId);
                }
            } else {
                showInviteToast((d.fromPlayerName || 'Oyuncu') + ' daveti reddetti');
            }
        });

        // Uygulamadan gelen davet olayları (token nativeBridge'de önceden uygulanır).
        const unsubscribeNative = subscribeNativeEvents(({ handler, payload } = {}) => {
            // Davet eden gelmeyecek: davet masasının bekletmesi hemen biter.
            if (handler === 'inviteRelease') { socket.emit('invite:release'); return; }
            // "Masaya geç": açık sayfada yeni sayfa açılmaz, buradan oturulur.
            if (handler !== 'inviteJoin' || !payload?.roomId) return;
            if (payload.roomId === seatedRoomRef.current) return;
            closeInvitePanel();
            dismissGameInviteDialog();
            if (seatedRoomRef.current) {
                socket.emit('leaveQueue');
                seatedRoomRef.current = null;
            }
            if (seatRef.current) seatRef.current(payload.roomId);
        });

        return () => {
            unsubscribeNative();
            dismissGameInviteDialog();
            closeInvitePanel();
            socket.disconnect();
            socketRef.current = null;
        };
    }, [nativeProfile, navigate]);

    const seat = (roomId) => {
        const socket = socketRef.current;
        if (!socket) return;
        const name = nameInput.trim() || getNoun();
        updatePlayerName(name);
        setLobbyError('');
        setPhase('connecting');

        const payload = {
            mode: modeRef.current,
            displayName: name,
            userId: nativeProfile?.userId || '',
            id: nativeProfile?.id || '',
            uuid: nativeProfile?.uuid || '',
            avatarUrl: nativeProfile?.avatarUrl || '',
            callerId: nativeProfile?.callerId || '',
            gender: nativeProfile?.gender || '',
            rating: nativeProfile?.rating || 0,
            interests: nativeProfile?.interests || [],
            numericId: nativeProfile?.numericId || nativeProfile?.id || '',
        };
        currentRoomRef.current = roomId || null;
        if (roomId) socket.emit('joinRoom', { ...payload, roomId, ...inviteJoinProof(roomId) });
        else socket.emit('joinQueue', payload);
        setPhase('waiting');
    };
    seatRef.current = seat;

    const handlePlay = () => seat(null);
    const handleJoinRoom = (roomId) => seat(roomId);

    // Watching costs nothing and needs no seat, so it skips the lobby handshake
    // entirely and goes straight to the table in spectator mode.
    const handleWatchRoom = (roomId) => {
        const name = nameInput.trim() || getNoun();
        updatePlayerName(name);
        navigate(`/game/${roomId}?spectate=1`);
    };

    // Back to a seat that is already yours: no queue, no second entry fee — the
    // game socket recognises the seat and takes the bot back out of it.
    const handleReturnToRoom = (roomId) => {
        navigate(`/game/${roomId}`);
    };

    const handleCancel = () => {
        socketRef.current?.emit('leaveQueue');
        currentRoomRef.current = null;
        seatedRoomRef.current = null;
        setPhase('idle');
        setLobbyData(null);
    };

    // Masada bekliyorsa davet o masaya gider; değilse backend yeni özel masa üretir.
    const handleOpenInvitePanel = () => {
        openInvitePanel({
            socket: socketRef.current,
            seatedRoomId: seatedRoomRef.current,
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && phase === 'idle') handlePlay();
    };

    return (
        <div className={styles.page}>
            {/* Sol üst çıkış: önce onay dialogu (logout_dialog tasarımı);
                Çıkış Yap aynı exitGame köprüsünü koşturur. Coin barı üst-ortada
                Flutter'da kalır. */}
            <button
                type="button"
                className={styles.exitBtn}
                onClick={() => setExitConfirm(true)}
                aria-label="Çıkış"
            >
                <img src={imgExit} alt="" draggable={false} />
            </button>
            <ExitConfirmDialog
                open={exitConfirm}
                onCancel={() => setExitConfirm(false)}
                onConfirm={() => { setExitConfirm(false); postNative('exitGame', {}); }}
            />

            <div className={styles.centerContainer}>
                {/* Üst bant coin barına ayrılı; logo onun altına oturur */}
                <img className={styles.logo} src={imgLogo} alt="BATAK" draggable={false} />

                {lobbyError && <div className={styles.lobbyError}>{lobbyError}</div>}

                {phase === 'idle' && (
                    <>
                        <input
                            className={styles.nameInput}
                            value={nameInput}
                            onChange={e => { if (!nativeProfile?.playerName) setNameInput(e.target.value); }}
                            onKeyDown={handleKeyDown}
                            maxLength={20}
                            placeholder="İsmin..."
                            readOnly={!!nativeProfile?.playerName}
                            autoFocus={!nativeProfile?.playerName}
                        />

                        <button type="button" className={styles.quickPlay} onClick={handlePlay}>
                            <img src={imgQuickPlay} alt="Hızlı Oyna" draggable={false} />
                        </button>

                        {/* Oyun modu kartları — masaları filtreler ve Hızlı
                            Oyna'nın hangi masaya oturacağını belirler. Yazılar
                            görselde bakılı; seçili olana onay rozeti konur. */}
                        <div className={styles.modeTabs}>
                            {[
                                { id: 'partner', img: imgModeEsli, label: 'Eşli' },
                                { id: 'solo', img: imgModeTekli, label: 'Tekli' },
                                { id: 'gomme', img: imgModeGommeli, label: 'Gömmeli' },
                            ].map(m => (
                                <button
                                    type="button"
                                    key={m.id}
                                    className={`${styles.modeTab} ${mode === m.id ? styles.modeTabActive : ''}`}
                                    onClick={() => {
                                        setMode(m.id);
                                        try { localStorage.setItem('batak_mode', m.id); } catch (e) {}
                                    }}
                                >
                                    <img src={m.img} alt={m.label} draggable={false} />
                                    {mode === m.id && (
                                        <img className={styles.modeCheck} src={imgModeCheck} alt="" draggable={false} />
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className={styles.roomsHeader}>
                            <img src={imgMasalar} alt="Masalar" draggable={false} />
                            <button
                                type="button"
                                className={styles.refreshBtn}
                                onClick={() => socketRef.current?.emit('listRooms')}
                                aria-label="Masaları yenile"
                            >
                                {/* ⟳ glifi daire içinde cılız kalıyordu — dolduran SVG */}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21.5 4.5v5h-5" />
                                    <path d="M20.49 14a8.5 8.5 0 1 1-2-8.85L21.5 8" />
                                </svg>
                            </button>
                        </div>
                        <RoomList
                            rooms={openRooms.filter(r => (r.mode || 'partner') === mode)}
                            onJoin={handleJoinRoom}
                            onWatch={handleWatchRoom}
                            onReturn={handleReturnToRoom}
                            myUserId={nativeProfile?.userId || ''}
                            busy={phase !== 'idle'}
                        />

                        {/* Alt sıra (tasarım): Nasıl oynanır? + Arkadaşlarını davet et */}
                        <BatakTutorial />
                        <button
                            type="button"
                            className={styles.inviteButton}
                            onClick={handleOpenInvitePanel}
                        >
                            <img src={imgInvite} alt="Arkadaşlarını Davet Et" draggable={false} />
                        </button>
                    </>
                )}

                {phase === 'connecting' && (
                    <div className={styles.connectingMsg}>Bağlanıyor...</div>
                )}

                {phase === 'waiting' && (
                    <LobbyOverlay
                        lobbyData={lobbyData}
                        playerName={nameInput.trim() || playerName}
                        onCancel={handleCancel}
                        onInvite={handleOpenInvitePanel}
                    />
                )}
            </div>
        </div>
    );
};

export default MainPage;
